/**
 * Date Utilities
 * Handles conversion between ISO dates (YYYY-MM-DD) and Latin American format (dd/mm/yyyy)
 */

import { format, parse, isValid } from 'date-fns';
import { es } from 'date-fns/locale';

/**
 * Format an ISO date string (YYYY-MM-DD) to dd/mm/yyyy
 * @param {string} isoDate - ISO format date string
 * @returns {string} Date in dd/mm/yyyy format
 */
export function formatToDDMMYYYY(isoDate) {
    if (!isoDate) return '';

    try {
        const date = parse(isoDate, 'yyyy-MM-dd', new Date());
        if (!isValid(date)) return '';
        return format(date, 'dd/MM/yyyy');
    } catch (error) {
        console.error('Error formatting date:', error);
        return '';
    }
}

/**
 * Parse dd/mm/yyyy string to ISO format (YYYY-MM-DD)
 * @param {string} ddmmyyyy - Date in dd/mm/yyyy format
 * @returns {string} ISO format date string
 */
export function parseFromDDMMYYYY(ddmmyyyy) {
    if (!ddmmyyyy) return '';

    try {
        const date = parse(ddmmyyyy, 'dd/MM/yyyy', new Date());
        if (!isValid(date)) return '';
        return format(date, 'yyyy-MM-dd');
    } catch (error) {
        console.error('Error parsing date:', error);
        return '';
    }
}

/**
 * Check if a date string is valid
 * @param {string} dateString - Date string to validate
 * @param {string} formatString - Expected format (default: 'yyyy-MM-dd')
 * @returns {boolean} True if valid
 */
export function isValidDate(dateString, formatString = 'yyyy-MM-dd') {
    if (!dateString) return false;

    try {
        const date = parse(dateString, formatString, new Date());
        return isValid(date);
    } catch {
        return false;
    }
}

/**
 * Get localized month name
 * @param {number} monthIndex - Month index (0-11)
 * @param {string} locale - Locale code (default: 'es')
 * @returns {string} Month name
 */
export function getMonthName(monthIndex, locale = 'es') {
    const date = new Date(2000, monthIndex, 1);
    return format(date, 'MMMM', { locale: es });
}

/**
 * Format a Date object for display
 * @param {Date} date - Date object
 * @param {string} formatStr - Format string (default: 'dd/MM/yyyy')
 * @returns {string} Formatted date string
 */
export function formatDate(date, formatStr = 'dd/MM/yyyy') {
    if (!date || !isValid(date)) return '';
    return format(date, formatStr, { locale: es });
}

/**
 * Convert ISO string to Date object
 * @param {string} isoString - ISO date string
 * @returns {Date|null} Date object or null if invalid
 */
export function isoToDate(isoString) {
    if (!isoString) return null;

    try {
        const date = parse(isoString, 'yyyy-MM-dd', new Date());
        return isValid(date) ? date : null;
    } catch {
        return null;
    }
}

/**
 * Convert Date object to ISO string
 * @param {Date} date - Date object
 * @returns {string} ISO date string
 */
export function dateToISO(date) {
    if (!date || !isValid(date)) return '';
    return format(date, 'yyyy-MM-dd');
}
